// Stable Hymn identity across manifest refreshes (issue #3517 item A3).
//
// Every hymnal manifest refresh rebuilds every Hymn object from scratch, so any refresh - even one
// that changed a single song in an unrelated hymnal - handed React brand new objects for every
// song. Effects and memos keyed on a hymn object (e.g. the current song's notation fetch) then
// re-ran and threw away state such as a network error, a parsed file or a scroll position.
//
// These helpers reconcile a freshly built list against the previously produced one and hand back
// the PREVIOUS object for every song that did not actually change, so only the songs that really
// changed get new identities. React-free on purpose: pure functions, unit tested directly.
import { CustomMusic, Hymn } from '../../../common/src/model.ts';
import { deepEqual } from '../../../common/src/util.ts';
import { HymnalWithHymns } from './hymnals.ts';

// Two builds of the same song are the same song when the manifest content hash matches (the hash
// covers every manifest-derived field), falling back to a field comparison when either side has no
// hash - custom music entries and hymns rebuilt by an older client may not carry one.
export function isSameHymn(previous: Hymn, next: Hymn): boolean {
  if (previous === next) {
    return true;
  }
  if (
    previous.slug !== next.slug ||
    previous.songNumber !== next.songNumber ||
    previous.hymnal !== next.hymnal ||
    previous.basePath !== next.basePath
  ) {
    return false;
  }
  if (previous.hash && next.hash) {
    return previous.hash === next.hash;
  }
  // manifest-derived plain data on both sides; deepEqual treats an absent key and an undefined
  // one as equal, which is what hymns built by spreading conditional properties need
  return deepEqual(previous, next);
}

// Reuses the previous object for every unchanged hymn, and the previous ARRAY when nothing changed.
export function reconcileHymns(previous: Hymn[] | undefined, next: Hymn[]): Hymn[] {
  if (!previous) {
    return next;
  }
  const previousBySlug = new Map<string, Hymn>();
  for (const hymn of previous) {
    if (!previousBySlug.has(hymn.slug)) {
      previousBySlug.set(hymn.slug, hymn);
    }
  }
  const reconciled = next.map((hymn) => {
    const previousHymn = previousBySlug.get(hymn.slug);
    return previousHymn && isSameHymn(previousHymn, hymn) ? previousHymn : hymn;
  });
  if (reconciled.length === previous.length && reconciled.every((hymn, index) => hymn === previous[index])) {
    return previous;
  }
  // nothing was reused (the first population, or a wholesale replacement): hand back the new list
  // itself rather than an equal copy of it
  if (reconciled.every((hymn, index) => hymn === next[index])) {
    return next;
  }
  return reconciled;
}

// The fields of a hymnal other than its hymns, which are compared separately (and by identity).
// `message` is a JSX element and `songList` may carry one, so they are compared by reference
// rather than walked - a deep walk of a React element can reach a fiber and never terminate.
function isSameHymnalApartFromHymns(previous: HymnalWithHymns, next: HymnalWithHymns): boolean {
  const { hymns: _previousHymns, message: previousMessage, ...previousRest } = previous;
  const { hymns: _nextHymns, message: nextMessage, ...nextRest } = next;
  if (previousMessage !== nextMessage) {
    return false;
  }
  return deepEqual(previousRest, nextRest);
}

function getHymnalKey(hymnal: HymnalWithHymns): string {
  return `${hymnal.id}|${hymnal.slug}`;
}

// Reuses the previous hymnal object whenever none of its hymns and none of its other fields
// changed, and the previous ARRAY when that held for every hymnal.
export function reconcileHymnals(previous: HymnalWithHymns[] | undefined, next: HymnalWithHymns[]): HymnalWithHymns[] {
  if (!previous) {
    return next;
  }
  const previousByKey = new Map<string, HymnalWithHymns>();
  for (const hymnal of previous) {
    if (!previousByKey.has(getHymnalKey(hymnal))) {
      previousByKey.set(getHymnalKey(hymnal), hymnal);
    }
  }
  const reconciled = next.map((hymnal) => {
    const previousHymnal = previousByKey.get(getHymnalKey(hymnal));
    if (!previousHymnal) {
      return hymnal;
    }
    const hymns = reconcileHymns(previousHymnal.hymns, hymnal.hymns);
    if (hymns === previousHymnal.hymns && isSameHymnalApartFromHymns(previousHymnal, hymnal)) {
      return previousHymnal;
    }
    return hymns === hymnal.hymns ? hymnal : { ...hymnal, hymns };
  });
  if (reconciled.length === previous.length && reconciled.every((hymnal, index) => hymnal === previous[index])) {
    return previous;
  }
  if (reconciled.every((hymnal, index) => hymnal === next[index])) {
    return next;
  }
  return reconciled;
}

// Per-org custom music lists, reconciled org by org, reusing the previous map when nothing changed.
export function reconcileCustomMusic(previous: CustomMusic | undefined, next: CustomMusic): CustomMusic {
  if (!previous) {
    return next;
  }
  const reconciled: CustomMusic = Object.fromEntries(
    Object.entries(next).map(([orgId, hymns]) => [orgId, reconcileHymns(previous[orgId], hymns)]),
  );
  const previousKeys = Object.keys(previous);
  const isUnchanged =
    previousKeys.length === Object.keys(reconciled).length &&
    previousKeys.every((orgId) => reconciled[orgId] === previous[orgId]);
  return isUnchanged ? previous : reconciled;
}
